import { fail } from './errors.js';
import { won } from './money.js';

/** 센터 직원(치료사·센터장). Cognito 사용자(sub)와 같은 id 를 쓴다. */
export interface Staff {
  userId: string;
  centerId: string;
  name: string;
  title: string; // 직함(언어재활사, 놀이치료사 …)
  role: 'CENTER_DIRECTOR' | 'THERAPIST';
  active: boolean;
}

export interface Goal { id: string; text: string; term: 'SHORT' | 'LONG' }

/** 민감정보(평문). 저장할 때는 반드시 암호화한다. */
export interface ClientSensitive {
  disabilityType: string;
  guardianPhone: string;
  address: string;
  memo: string;
}

export interface ClientInput {
  displayName: string;
  birthYear: number;
  unitPrice: unknown;
  therapistIds: string[];
  goals: Array<{ id?: string; text: string; term: 'SHORT' | 'LONG' }>;
  /** 목표 기간(선택). 시작일·종료일 둘 다 YYYY-MM-DD. */
  goalPeriod?: GoalPeriod | null;
}

export interface GoalPeriod { start: string; end: string }

const realDate = (s: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
};

/** 목표 기간 검증: 실제 있는 날짜, 시작 ≤ 종료, 최대 5년. */
export function validateGoalPeriod(p: GoalPeriod): GoalPeriod {
  const start = p.start.trim(), end = p.end.trim();
  if (!realDate(start) || !realDate(end)) fail('VALIDATION', '목표 기간은 2026-03-01처럼 날짜로 입력하세요.');
  if (start > end) fail('VALIDATION', '목표 기간의 시작일이 종료일보다 늦습니다.');
  if (Number(end.slice(0, 4)) - Number(start.slice(0, 4)) > 5) fail('VALIDATION', '목표 기간은 5년 이내로 입력하세요.');
  return { start, end };
}

const PHONE_RE = /^0\d{1,2}-?\d{3,4}-?\d{4}$/;

/**
 * 이용자 기본정보 검증·정규화.
 * - 담당 치료사는 이 센터의 활동 중인 치료사·센터장만
 * - 단가는 정수 원(회기당 100만 원 이하)
 * - 목표는 최대 10개, 각 200자
 */
export function validateClient(input: ClientInput, staff: readonly Staff[], newId: () => string, thisYear: number) {
  const displayName = input.displayName.trim();
  if (displayName.length < 1 || displayName.length > 30) fail('VALIDATION', '이름은 1~30자로 입력하세요.');
  if (!Number.isInteger(input.birthYear) || input.birthYear < thisYear - 40 || input.birthYear > thisYear) {
    fail('VALIDATION', `출생연도는 ${thisYear - 40}~${thisYear} 사이로 입력하세요.`);
  }
  const unitPrice = won(input.unitPrice);
  if (unitPrice < 0 || unitPrice > 1_000_000) fail('VALIDATION', '회기 단가는 0~1,000,000원 사이로 입력하세요.');
  const active = new Set(staff.filter((s) => s.active).map((s) => s.userId));
  const therapistIds = [...new Set(input.therapistIds)];
  if (therapistIds.length > 5) fail('VALIDATION', '담당 선생님은 5명까지 지정할 수 있습니다.');
  for (const id of therapistIds) if (!active.has(id)) fail('VALIDATION', '이 센터의 선생님만 담당으로 지정할 수 있습니다.');
  if (input.goals.length > 10) fail('VALIDATION', '목표는 10개까지 등록할 수 있습니다.');
  const goals: Goal[] = input.goals.map((g) => {
    const text = g.text.trim();
    if (!text || text.length > 200) fail('VALIDATION', '목표는 1~200자로 입력하세요.');
    if (g.term !== 'SHORT' && g.term !== 'LONG') fail('VALIDATION', '목표 기간은 단기 또는 장기입니다.');
    return { id: g.id && /^[\w-]{1,40}$/.test(g.id) ? g.id : newId(), text, term: g.term };
  });
  // goalPeriod 를 보내지 않으면(undefined) 기존 값 유지, null 이면 지운다
  const goalPeriod = input.goalPeriod === undefined ? {} : { goalPeriod: input.goalPeriod === null ? undefined : validateGoalPeriod(input.goalPeriod) };
  return { displayName, birthYear: input.birthYear, unitPrice, therapistIds, goals, ...goalPeriod };
}

export function validateSensitive(s: ClientSensitive): ClientSensitive {
  const out = {
    disabilityType: s.disabilityType.trim().slice(0, 60),
    guardianPhone: s.guardianPhone.trim(),
    address: s.address.trim().slice(0, 120),
    memo: s.memo.trim().slice(0, 1000),
  };
  if (out.guardianPhone && !PHONE_RE.test(out.guardianPhone)) fail('VALIDATION', '보호자 연락처 형식을 확인하세요. 예: 010-1234-5678');
  return out;
}
